/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import React, { useContext, useRef, useState } from "react";
import { Position } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {
  findNodeHandleForEntry,
  TransformEngineNodeData,
  TransformEngineNodeEntry,
} from "../Util/TransformEngineDataUtils";
import {
  DirectionalStack,
  HorizontalSpacer,
} from "../../../DesignSystem/Containers";
import { useTransformEngineStyles } from "../TransformEngineStyles";
import { createHandleComponentsForHandle } from "../TransformEngineNodeComponent";
import { ProjectContext } from "../../../UserApp";

export interface HandleRefTuple {
  inputHandle: HTMLDivElement | null;
  inputPosition: Position | null;
  outputHandle: HTMLDivElement | null;
  outputPosition: Position | null;
}

export interface TransformEngineNodeEntryProps {
  entry: TransformEngineNodeEntry;
}

export interface TransformEngineNodeProps {
  data: TransformEngineNodeData;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  subtitle?: React.ReactNode;
  mainComponent?: React.ReactNode;
}

export function XmlToProtoTransformComponent_Prim(
  props: TransformEngineNodeProps
): JSX.Element {
  const {
    PARALLELOGRAM_NODE_STYLE,
    NODE_HEADER_TEXT_STYLE,
    NODE_SUBTITLE_TEXT_STYLE,
    SELECTED_NODE_BORDER_STYLE,
  } = useTransformEngineStyles();

  const data = props.data;
  const { projectState } = useContext(ProjectContext);

  const handleRefs = useRef(new Map<number, HandleRefTuple>());

  const [_, setIsDraggingHandle] = useState(false);

  const { rfNodes } = projectState;
  const node = rfNodes.find((node) => node.id == data.dataID);
  const isSelected = node?.selected ?? false;

  // create one node with as many entries as it needs
  return (
    <div
      data-testid={`${data.dataID}-granular-node`}
      style={{
        ...PARALLELOGRAM_NODE_STYLE,
        ...(isSelected ? SELECTED_NODE_BORDER_STYLE : {}),
      }}
      tabIndex={0}
    >
      <DirectionalStack direction="row">
        {data.title ? (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              width: "100%",
            }}
          >
            <div
              style={{
                ...NODE_HEADER_TEXT_STYLE,
                fontSize: "1.3rem",
                padding: "2px",
                paddingRight: "1px",
                transform: "skew(-20deg)",
              }}
            >
              {data.title.toLocaleLowerCase()}
            </div>
            <div style={NODE_SUBTITLE_TEXT_STYLE}>granular</div>
          </div>
        ) : (
          <>
            <HorizontalSpacer width="10px"></HorizontalSpacer>{" "}
            <div>Undefined</div>
          </>
        )}
      </DirectionalStack>
      {
        // create handles and assign handle refs so they can be moved into position
        data.entries.map((node) => {
          const refTuple: HandleRefTuple = {
            inputHandle: null,
            inputPosition: null,
            outputHandle: null,
            outputPosition: null,
          };
          handleRefs.current.set(node.id as number, refTuple);

          const handle = findNodeHandleForEntry(data, node.id);

          return createHandleComponentsForHandle(
            handle,
            node.path.pathType,
            refTuple,
            undefined,
            setIsDraggingHandle
          );
        })
      }
    </div>
  );
}
